export type TeamCertification = {
  name: string;
  issuedBy?: string;
  expiresAt?: string | null;
  notes?: string;
};

export type TeamEmergencyContact = {
  name?: string;
  phone?: string;
  relationship?: string;
};

export type TeamMemberSummary = {
  _id: string;
  name: string;
  email: string;
  role: 'admin' | 'technician';
  jobTitle?: string;
  department?: string;
  phone?: string;
  avatarBase64?: string;
  certifications?: TeamCertification[];
  status: 'active' | 'inactive';
  hiredAt?: string | null;
};

export type TeamMemberDetail = TeamMemberSummary & {
  bio?: string;
  emergencyContact?: TeamEmergencyContact;
  payrollEmployeeCode?: string;
  mustChangePassword?: boolean;
  googleConnected?: boolean;
  googleEmail?: string;
  lastLogin?: string | null;
  createdAt?: string;
  updatedAt?: string;
};

export type CertDisplayStatus = 'none' | 'valid' | 'expiring' | 'expired';

export function certDisplayStatus(expiresAt?: string | null): CertDisplayStatus {
  if (!expiresAt) return 'none';
  const date = new Date(expiresAt);
  if (Number.isNaN(date.getTime())) return 'none';
  const now = Date.now();
  if (date.getTime() < now) return 'expired';
  const thirtyDays = 30 * 24 * 60 * 60 * 1000;
  if (date.getTime() - now <= thirtyDays) return 'expiring';
  return 'valid';
}

export function certStatusLabel(status: CertDisplayStatus): string {
  switch (status) {
    case 'none':
      return 'No expiry';
    case 'valid':
      return 'Valid';
    case 'expiring':
      return 'Expiring soon';
    default:
      return 'Expired';
  }
}

export function truncateText(value: string, max: number): string {
  const trimmed = value.trim();
  if (trimmed.length <= max) return trimmed;
  return `${trimmed.slice(0, max - 1)}…`;
}

export function initialsFromName(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return '?';
  return trimmed.charAt(0).toUpperCase();
}

export function formatDisplayDate(value?: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString(undefined, { dateStyle: 'medium' });
}

export function isValidPhone(value: string): boolean {
  if (!value.trim()) return true;
  const digits = value.replace(/\D/g, '');
  return digits.length >= 10 && digits.length <= 15;
}

export function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error('Failed to read image.'));
    reader.readAsDataURL(file);
  });
}
